import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getStoredTokens, signOut } from '../../auth/auth';
import { withReturnTo } from '../../lib/returnTo';
import Spinner from '../../components/Spinner';

/** port は Unity エディタが立てるループバック HTTP サーバの待受ポート（well-known 範囲を避ける）。 */
function isValidPort(value: string | null): boolean {
  if (!value || !/^[0-9]+$/.test(value)) return false;
  const port = Number(value);
  return port >= 1024 && port <= 65535;
}

/** state は Unity 側が発行する一回性のトークン文字列。 */
function isValidState(value: string | null): boolean {
  return !!value && /^[A-Za-z0-9_-]{1,256}$/.test(value);
}

type ConnectStatus = 'idle' | 'connecting' | 'success' | 'error' | 'no-refresh-token';

/**
 * Unity エディタ拡張との連携用ページ（`/unity/auth?port=...&state=...`）。
 * Unity がローカルに立てたループバック HTTP サーバの port と、一回性の state をクエリで受け取り、
 * サイトに現在ログイン中のユーザーの Cognito トークン（idToken / refreshToken）を
 * その宛先へ POST して受け渡す。トークンの refresh はここでは行わない
 * （getStoredTokens() は保存済みの値をそのまま返す）。
 */
export default function UnityAuth() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const portParam = searchParams.get('port');
  const stateParam = searchParams.get('state');
  const paramsValid = isValidPort(portParam) && isValidState(stateParam);

  const [status, setStatus] = useState<ConnectStatus>('idle');

  // 未ログインなら、この画面に戻ってこられるよう現在の URL を携えてログイン画面へ。
  // パラメータが不正な場合はログインチェックより前に弾くため、ここでは何もしない。
  // state に加えクエリにも from を載せる（新規登録を挟む・リロードされる経路でも失われないよう）。
  useEffect(() => {
    if (paramsValid && ready && !user) {
      const from = `${location.pathname}${location.search}`;
      navigate(withReturnTo('/login', from), {
        state: { from },
        replace: true,
      });
    }
  }, [paramsValid, ready, user, navigate, location]);

  if (!paramsValid) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-card__title">連携できません</h1>
          <div className="auth-form__error">
            不正なリクエストです。Unity エディタのツールウィンドウから開き直してください。
          </div>
        </div>
      </div>
    );
  }

  if (!ready || (ready && !user)) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Spinner />
        </div>
      </div>
    );
  }

  const connect = async () => {
    setStatus('connecting');
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) {
      setStatus('no-refresh-token');
      return;
    }
    try {
      const res = await fetch(`http://127.0.0.1:${portParam}/vrma-hub-auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: stateParam, refreshToken: tokens.refreshToken, idToken: tokens.idToken }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const reLogin = () => {
    signOut();
    const from = `${location.pathname}${location.search}`;
    navigate(withReturnTo('/login', from), { state: { from } });
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-card__title">Unity と連携</h1>
        <p className="auth-card__lead">
          Unity エディタの VRMA Hub ツールがあなたのアカウントでログインしようとしています。
        </p>
        <p>
          ログイン中のアカウント: <strong>{user!.email}</strong>
        </p>

        {status === 'success' && (
          <div className="auth-form__success">
            連携が完了しました。Unity エディタに戻ってください。このタブは閉じて構いません。
          </div>
        )}

        {status === 'no-refresh-token' && (
          <div className="auth-form__error">
            再ログインが必要です。セッション情報が古いため、このまま連携できません。お手数ですが再度ログインしてください。
          </div>
        )}

        {status === 'error' && (
          <div className="auth-form__error">
            Unity 側の待ち受けが見つかりませんでした。Unity のツールウィンドウで「ブラウザでログイン」を押した直後にこのページを開いたか確認してください。
            Chrome / Edge / Firefox でお試しください（Safari では失敗することがあります）。
          </div>
        )}

        {status === 'no-refresh-token' ? (
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button type="button" className="btn btn--primary" onClick={reLogin}>
              再ログイン
            </button>
          </div>
        ) : status === 'success' ? null : (
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button
              type="button"
              className="btn"
              onClick={() => navigate('/')}
              disabled={status === 'connecting'}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={connect}
              disabled={status === 'connecting'}
            >
              {status === 'connecting' ? '連携中…' : status === 'error' ? '再試行' : 'Unity と連携する'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
